import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { LogOut, Plus, Edit2, Trash2, Save, X, MessageSquare, DollarSign, Users, Car, Upload, ImagePlus } from "lucide-react";
import PikiLogo from "@/components/PikiLogo";

const EMPTY_VEHICLE = {
  id: "", name: "", category: "Compact", tagline: "", daily_rate: 0, weekly_rate: 0,
  passengers: 5, luggage: "2 Bags", doors: 4, transmission: "Automatic",
  ac: true, fuel: "Petrol", features: [], primary_image: "", gallery: [], available: true,
};

const resolveVehicleImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  const base = API.replace(/\/api$/, "");
  return u.startsWith("/api/") ? `${base}${u}` : u;
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [rates, setRates] = useState(null);
  const [site, setSite] = useState(null);
  const [editVehicle, setEditVehicle] = useState(null);
  const email = localStorage.getItem("piki_admin_email");

  useEffect(() => {
    if (!localStorage.getItem("piki_admin_token")) { nav("/admin/login"); return; }
    loadAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    try {
      const [v, b, c, r, s] = await Promise.all([
        api.get("/vehicles"), api.get("/bookings"), api.get("/contact"), api.get("/rates"), api.get("/site"),
      ]);
      setVehicles(v.data); setBookings(b.data); setContacts(c.data); setRates(r.data); setSite(s.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("piki_admin_token"); nav("/admin/login"); }
      else toast.error("Failed to load data");
    }
  };

  const logout = () => { localStorage.removeItem("piki_admin_token"); localStorage.removeItem("piki_admin_email"); nav("/admin/login"); };

  const saveVehicle = async () => {
    try {
      if (editVehicle.id && vehicles.find((v) => v.id === editVehicle.id)) {
        await api.patch(`/vehicles/${editVehicle.id}`, editVehicle);
        toast.success("Vehicle updated");
      } else {
        const payload = { ...editVehicle };
        if (!payload.id) delete payload.id;
        await api.post("/vehicles", payload);
        toast.success("Vehicle added");
      }
      setEditVehicle(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const deleteVehicle = async (id) => {
    if (!confirm("Delete this vehicle?")) return;
    try { await api.delete(`/vehicles/${id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const updateBookingStatus = async (id, status) => {
    try { await api.patch(`/bookings/${id}`, { status }); toast.success("Status updated"); loadAll(); }
    catch { toast.error("Update failed"); }
  };

  const saveRates = async () => {
    try { await api.put("/rates", rates); toast.success("Rates saved"); }
    catch { toast.error("Save failed"); }
  };
  const saveSite = async () => {
    try { await api.put("/site", site); toast.success("Site info saved"); }
    catch { toast.error("Save failed"); }
  };

  const stats = [
    { icon: Car, label: "Vehicles", value: vehicles.length },
    { icon: MessageSquare, label: "Bookings", value: bookings.length },
    { icon: Users, label: "Contacts", value: contacts.length },
    { icon: DollarSign, label: "Pending", value: bookings.filter((b) => b.status === "pending").length },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-[#2E2E2E] bg-[#050505]/90 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PikiLogo className="h-11 w-auto" />
            <div className="text-[10px] text-[#737373] font-mono-piki uppercase tracking-widest">Admin · {email}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => nav("/")} className="text-xs px-3 py-1.5 border border-[#2E2E2E] rounded-lg text-[#A3A3A3] hover:text-white">View Site</button>
            <button onClick={logout} className="text-xs px-3 py-1.5 border border-[#2E2E2E] rounded-lg text-[#A3A3A3] hover:text-white flex items-center gap-1.5" data-testid="admin-logout-btn"><LogOut className="w-3 h-3" /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <div key={i} className="piki-card p-5" data-testid="admin-dashboard-stats-card">
              <s.icon className="w-5 h-5 text-[#D9A62E]" />
              <div className="mt-3 text-3xl font-extrabold font-mono-piki">{s.value}</div>
              <div className="text-xs text-[#737373] uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-8 flex flex-wrap gap-2 border-b border-[#2E2E2E]">
          {["vehicles", "bookings", "contacts", "rates", "site"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`px-4 py-3 text-sm font-semibold capitalize border-b-2 -mb-px transition-colors ${tab === t ? "border-[#D9A62E] text-[#D9A62E]" : "border-transparent text-[#A3A3A3] hover:text-white"}`}>
              {t}
            </button>
          ))}
        </div>

        {/* Vehicles */}
        {tab === "vehicles" && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold">Fleet Manager</h2>
              <button onClick={() => setEditVehicle({ ...EMPTY_VEHICLE })} className="piki-gold-btn text-sm flex items-center gap-2" data-testid="admin-add-vehicle-btn"><Plus className="w-4 h-4" /> Add Vehicle</button>
            </div>
            <div className="piki-card overflow-hidden" data-testid="admin-vehicles-table">
              <div className="grid grid-cols-12 gap-3 p-4 text-xs uppercase tracking-widest text-[#737373] font-mono-piki border-b border-[#2E2E2E]">
                <div className="col-span-5">Vehicle</div><div className="col-span-2">Category</div><div className="col-span-2">Daily</div><div className="col-span-2">Status</div><div className="col-span-1"></div>
              </div>
              {vehicles.map((v) => (
                <div key={v.id} className="grid grid-cols-12 gap-3 p-4 items-center border-b border-[#2E2E2E]/60 last:border-0">
                  <div className="col-span-5 flex items-center gap-3">
                    <img src={resolveVehicleImg(v.primary_image)} alt="" className="w-14 h-10 rounded object-cover" />
                    <div><div className="font-semibold text-sm">{v.name}</div><div className="text-xs text-[#737373]">{v.passengers} pax · {v.transmission}</div></div>
                  </div>
                  <div className="col-span-2 text-sm text-[#A3A3A3]">{v.category}</div>
                  <div className="col-span-2 font-mono-piki text-[#D9A62E] font-semibold">${v.daily_rate}</div>
                  <div className="col-span-2"><span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${v.available ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>{v.available ? "Available" : "Off"}</span></div>
                  <div className="col-span-1 flex items-center gap-1 justify-end">
                    <button onClick={() => setEditVehicle(v)} className="p-1.5 hover:text-[#D9A62E]"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => deleteVehicle(v.id)} className="p-1.5 hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bookings */}
        {tab === "bookings" && (
          <div className="mt-6 piki-card overflow-hidden" data-testid="admin-bookings-table">
            <div className="grid grid-cols-12 gap-3 p-4 text-xs uppercase tracking-widest text-[#737373] font-mono-piki border-b border-[#2E2E2E]">
              <div className="col-span-3">Customer</div><div className="col-span-3">Vehicle</div><div className="col-span-2">Pickup</div><div className="col-span-2">Total</div><div className="col-span-2">Status</div>
            </div>
            {bookings.length === 0 && <div className="p-10 text-center text-[#737373] text-sm">No bookings yet.</div>}
            {bookings.map((b) => (
              <div key={b.id} className="grid grid-cols-12 gap-3 p-4 items-center border-b border-[#2E2E2E]/60 last:border-0 text-sm">
                <div className="col-span-3"><div className="font-semibold">{b.full_name}</div><div className="text-xs text-[#737373]">{b.email}</div><div className="text-xs text-[#737373]">{b.phone}</div></div>
                <div className="col-span-3 text-[#A3A3A3]">{b.vehicle_name || "—"}</div>
                <div className="col-span-2 text-xs text-[#A3A3A3] font-mono-piki">{b.pickup_datetime?.slice(0, 16).replace("T", " ")}</div>
                <div className="col-span-2 font-mono-piki text-[#D9A62E] font-semibold">${b.estimated_total?.toFixed?.(0) || "—"}</div>
                <div className="col-span-2">
                  <select value={b.status} onChange={(e) => updateBookingStatus(b.id, e.target.value)} data-testid="admin-booking-status-badge" className="bg-[#0F0F10] border border-[#2E2E2E] rounded-lg text-xs px-2 py-1.5 text-white">
                    <option value="pending">Pending</option><option value="confirmed">Confirmed</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contacts */}
        {tab === "contacts" && (
          <div className="mt-6 space-y-3">
            {contacts.length === 0 && <div className="p-10 text-center text-[#737373] text-sm">No messages yet.</div>}
            {contacts.map((c) => (
              <div key={c.id} className="piki-card p-5">
                <div className="flex items-center justify-between mb-2">
                  <div><div className="font-semibold">{c.name}</div><div className="text-xs text-[#737373]">{c.email} · {c.phone || "no phone"}</div></div>
                  <div className="text-xs text-[#737373] font-mono-piki">{c.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
                <p className="text-sm text-[#D4D4D4] mt-2 leading-relaxed">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* Rates */}
        {tab === "rates" && rates && (
          <div className="mt-6 space-y-6">
            <div className="piki-card p-6">
              <h3 className="font-display font-bold text-lg mb-4">Fees & Deposit</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Additional Driver" val={rates.additional_driver_fee} onChange={(v) => setRates({ ...rates, additional_driver_fee: Number(v) })} />
                <Field label="Deposit" val={rates.deposit} onChange={(v) => setRates({ ...rates, deposit: Number(v) })} />
                <Field label="Airport Delivery" val={rates.airport_delivery_fee} onChange={(v) => setRates({ ...rates, airport_delivery_fee: Number(v) })} />
              </div>
              <button onClick={saveRates} className="mt-5 piki-gold-btn text-sm">Save Rates</button>
            </div>
            <div className="piki-card p-6">
              <h3 className="font-display font-bold text-lg mb-4">Daily Tiers</h3>
              <div className="text-xs text-[#737373]">Edit tier prices below.</div>
              {rates.daily_tiers.map((t, i) => (
                <div key={i} className="grid grid-cols-4 gap-3 mt-3 items-center">
                  <input className="piki-input text-sm" value={t.category} onChange={(e) => { const dt = [...rates.daily_tiers]; dt[i] = { ...t, category: e.target.value }; setRates({ ...rates, daily_tiers: dt }); }} />
                  <input type="number" className="piki-input text-sm" value={t["1_day"]} onChange={(e) => { const dt = [...rates.daily_tiers]; dt[i] = { ...t, "1_day": Number(e.target.value) }; setRates({ ...rates, daily_tiers: dt }); }} />
                  <input type="number" className="piki-input text-sm" value={t["3_day"]} onChange={(e) => { const dt = [...rates.daily_tiers]; dt[i] = { ...t, "3_day": Number(e.target.value) }; setRates({ ...rates, daily_tiers: dt }); }} />
                  <input type="number" className="piki-input text-sm" value={t["7_day"]} onChange={(e) => { const dt = [...rates.daily_tiers]; dt[i] = { ...t, "7_day": Number(e.target.value) }; setRates({ ...rates, daily_tiers: dt }); }} />
                </div>
              ))}
              <button onClick={saveRates} className="mt-5 piki-gold-btn text-sm">Save Tiers</button>
            </div>
          </div>
        )}

        {/* Site */}
        {tab === "site" && site && (
          <div className="mt-6 piki-card p-6">
            <h3 className="font-display font-bold text-lg mb-4">Company Info</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {["company_name", "tagline", "phone", "whatsapp", "email", "address", "opening_hours"].map((k) => (
                <div key={k}>
                  <label className="text-xs text-[#A3A3A3] mb-1.5 block capitalize">{k.replace(/_/g, " ")}</label>
                  <input className="piki-input text-sm" value={site[k] || ""} onChange={(e) => setSite({ ...site, [k]: e.target.value })} />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">About</label>
                <textarea rows={4} className="piki-input text-sm" value={site.about || ""} onChange={(e) => setSite({ ...site, about: e.target.value })} />
              </div>
            </div>
            <button onClick={saveSite} className="mt-5 piki-gold-btn text-sm">Save Site Info</button>
          </div>
        )}
      </div>

      {/* Vehicle Editor */}
      {editVehicle && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#171717] border border-[#2E2E2E] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#2E2E2E] flex items-center justify-between sticky top-0 bg-[#171717]">
              <h3 className="font-display font-bold text-lg">{editVehicle.id ? "Edit Vehicle" : "Add Vehicle"}</h3>
              <button onClick={() => setEditVehicle(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField label="Name" val={editVehicle.name} onChange={(v) => setEditVehicle({ ...editVehicle, name: v })} />
              <div>
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">Category</label>
                <select className="piki-input text-sm" value={editVehicle.category} onChange={(e) => setEditVehicle({ ...editVehicle, category: e.target.value })}>
                  {["Compact", "Sedan", "SUV", "People Mover"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <TextField label="Tagline" val={editVehicle.tagline} onChange={(v) => setEditVehicle({ ...editVehicle, tagline: v })} full />
              <Field label="Daily Rate" val={editVehicle.daily_rate} onChange={(v) => setEditVehicle({ ...editVehicle, daily_rate: Number(v) })} />
              <Field label="Weekly Rate" val={editVehicle.weekly_rate} onChange={(v) => setEditVehicle({ ...editVehicle, weekly_rate: Number(v) })} />
              <Field label="Passengers" val={editVehicle.passengers} onChange={(v) => setEditVehicle({ ...editVehicle, passengers: Number(v) })} />
              <TextField label="Luggage" val={editVehicle.luggage} onChange={(v) => setEditVehicle({ ...editVehicle, luggage: v })} />
              <Field label="Doors" val={editVehicle.doors} onChange={(v) => setEditVehicle({ ...editVehicle, doors: Number(v) })} />
              <TextField label="Transmission" val={editVehicle.transmission} onChange={(v) => setEditVehicle({ ...editVehicle, transmission: v })} />
              <TextField label="Fuel" val={editVehicle.fuel} onChange={(v) => setEditVehicle({ ...editVehicle, fuel: v })} />
              <div className="flex items-center gap-3 pt-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editVehicle.ac} onChange={(e) => setEditVehicle({ ...editVehicle, ac: e.target.checked })} /> A/C</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editVehicle.available} onChange={(e) => setEditVehicle({ ...editVehicle, available: e.target.checked })} /> Available</label>
              </div>
              <ImageUploader
                label="Primary Image"
                value={editVehicle.primary_image}
                onChange={(url) => setEditVehicle({ ...editVehicle, primary_image: url })}
                full
              />
              <div className="sm:col-span-2">
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">Features (comma-separated)</label>
                <input className="piki-input text-sm" value={editVehicle.features.join(", ")} onChange={(e) => setEditVehicle({ ...editVehicle, features: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
              </div>
              <div className="sm:col-span-2">
                <GalleryUploader
                  images={editVehicle.gallery}
                  onChange={(gallery) => setEditVehicle({ ...editVehicle, gallery })}
                />
              </div>
            </div>
            <div className="p-6 border-t border-[#2E2E2E] flex items-center justify-end gap-3">
              <button onClick={() => setEditVehicle(null)} className="piki-outline-btn text-sm">Cancel</button>
              <button onClick={saveVehicle} className="piki-gold-btn text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, val, onChange }) {
  return (
    <div>
      <label className="text-xs text-[#A3A3A3] mb-1.5 block">{label}</label>
      <input type="number" className="piki-input text-sm" value={val} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function TextField({ label, val, onChange, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-xs text-[#A3A3A3] mb-1.5 block">{label}</label>
      <input className="piki-input text-sm" value={val || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// Build absolute URL for `/api/files/...` served by our backend
const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  const base = API.replace(/\/api$/, "");
  return u.startsWith("/api/") ? `${base}${u}` : u;
};

function ImageUploader({ label, value, onChange, full }) {
  const [busy, setBusy] = useState(false);
  const [urlMode, setUrlMode] = useState(false);
  const inputRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs text-[#A3A3A3]">{label}</label>
        <button type="button" onClick={() => setUrlMode(!urlMode)} className="text-[10px] text-[#737373] hover:text-[#D9A62E]">
          {urlMode ? "Use file upload" : "Paste URL instead"}
        </button>
      </div>
      {urlMode ? (
        <input className="piki-input text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://..." />
      ) : (
        <div className="flex items-center gap-3">
          <div className="w-24 h-16 rounded-lg border border-dashed border-[#2E2E2E] bg-[#0A0A0A] overflow-hidden flex items-center justify-center shrink-0">
            {value ? <img src={resolveUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-5 h-5 text-[#525252]" />}
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid="vehicle-image-upload-btn"
            className="piki-outline-btn text-xs flex items-center gap-2 disabled:opacity-50"
          >
            <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : value ? "Replace" : "Upload"}
          </button>
          {value && !busy && (
            <button type="button" onClick={() => onChange("")} className="text-xs text-[#737373] hover:text-[#EF4444]">Remove</button>
          )}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </div>
      )}
    </div>
  );
}

function GalleryUploader({ images, onChange }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} is over 8MB, skipping`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.url);
      }
      onChange([...images, ...uploaded]);
      if (uploaded.length) toast.success(`${uploaded.length} image(s) added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const removeAt = (i) => onChange(images.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs text-[#A3A3A3]">Gallery Images</label>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="vehicle-gallery-upload-btn"
          className="piki-outline-btn text-xs flex items-center gap-2 disabled:opacity-50"
        >
          <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Add Images"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
      </div>
      {images.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#2E2E2E] py-6 text-center text-xs text-[#737373]">
          No gallery images. Click <span className="text-[#D9A62E]">Add Images</span> to upload.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {images.map((u, i) => (
            <div key={i} className="relative group aspect-video rounded-lg overflow-hidden bg-[#0A0A0A] border border-[#2E2E2E]">
              <img src={resolveUrl(u)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[#050505]/80 border border-[#2E2E2E] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:border-[#EF4444] hover:text-[#EF4444]"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
